import { useMemo } from 'react';
import {
  UploadDetailSchema,
  UploadSummary,
  UploadSummarySchema,
} from '../../../../../services/entities/impl/blfApi/schemas/uploadSummary';
import {
  ValidationSummary,
  createValidationSummary,
} from '../../../utils/validation/createValidationSummary';
import { getValidationCounts } from '../../../utils/validation/getValidationCounts';
import {
  ValidationMessage,
  ValidationResult,
  validateSchema,
} from '../../../utils/validation/validateSchema';

/**
 * Hook for validating upload summary data
 *
 * @param uploadSummary The upload summary to validate
 * @returns Validation result and summary
 */
export const useUploadSummaryValidation = (
  uploadSummary: UploadSummary | undefined
): {
  validationResult: ValidationResult;
  summary: ValidationSummary;
} => {
  return useMemo(() => {
    // Check for empty upload summary data
    if (!uploadSummary) {
      const emptyValidationResult: ValidationResult = {
        valid: false,
        messages: [
          {
            severity: 'error',
            path: '',
            message:
              'No upload summary data found. API endpoint may have failed.',
          },
        ],
      };

      const counts = getValidationCounts(emptyValidationResult);

      return {
        validationResult: emptyValidationResult,
        summary: {
          result: createValidationSummary(counts),
          counts,
        },
      };
    }

    // 1. Validate the upload summary against schema
    const summaryValidationResult = validateSchema(
      uploadSummary,
      UploadSummarySchema
    );

    // 2. Validate each upload detail separately to get more specific messages
    const detailValidationMessages: ValidationMessage[] = [];

    if (uploadSummary.uploads && Array.isArray(uploadSummary.uploads)) {
      uploadSummary.uploads.forEach((uploadDetail, index) => {
        const detailResult = validateSchema(uploadDetail, UploadDetailSchema);

        // Add path prefix to each message for clarity
        if (!detailResult.valid) {
          detailResult.messages.forEach((message) => {
            detailValidationMessages.push({
              ...message,
              path: `uploads[${index}].${message.path}`,
            });
          });
        }
      });
    }

    // 3. Combine validation results
    const combinedMessages = [
      ...summaryValidationResult.messages,
      ...detailValidationMessages,
    ];

    const validationResult: ValidationResult = {
      valid:
        summaryValidationResult.valid && detailValidationMessages.length === 0,
      messages: combinedMessages,
    };

    // 4. Get validation counts
    const counts = getValidationCounts(validationResult);

    // 5. Create summary result with messages
    const summaryResult = createValidationSummary(counts);

    return {
      validationResult,
      summary: {
        result: summaryResult,
        counts,
      },
    };
  }, [uploadSummary]);
};
